import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Mic2, Pause, Play, Sparkles, Download, Wand2 } from "lucide-react";
import { DEMO_STEPS } from "@/lib/creatorConstants";

const LYRICS = "Jah is my shelter, my rock, my hiding place\nSing it in the morning, sing it in the night";
const CHIPS = ["Gospel Reggae", "Worship", "Gospel R&B", "Soul"];
const CHECKS = ["Creating instruments", "Generating vocals", "Mixing", "Finalizing"];

function Frame({ children }) {
  return (
    <div className="rounded-2xl border border-white/[0.12] bg-[#070a16] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] sm:p-5">
      <div className="mb-3 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
        <span className="ml-3 font-display text-[12px] font-semibold tracking-[0.18em] text-white/70">ELPM STUDIO · CREATE A SONG</span>
      </div>
      {children}
    </div>
  );
}

function WriteFrame({ active }) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!active) { setTyped(""); return undefined; }
    let i = 0;
    const id = setInterval(() => { i += 2; setTyped(LYRICS.slice(0, i)); if (i >= LYRICS.length) clearInterval(id); }, 45);
    return () => clearInterval(id);
  }, [active]);
  return (
    <Frame>
      <p className="font-display text-[13px] font-semibold text-white/85">Your lyrics or idea</p>
      <div className="mt-2 min-h-[88px] whitespace-pre-wrap rounded-xl border border-blue-400/40 bg-black/40 p-3 font-body text-[15px] leading-relaxed text-white">
        {typed}<span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-blue-300 align-middle" />
      </div>
      <p className="mt-3 text-[13px] text-white/70">Don't have lyrics? A title and a sentence is enough.</p>
    </Frame>
  );
}

function StyleFrame({ active }) {
  const [picked, setPicked] = useState(-1);
  const [voice, setVoice] = useState(-1);
  useEffect(() => {
    if (!active) { setPicked(-1); setVoice(-1); return undefined; }
    const a = setTimeout(() => setPicked(0), 700);
    const b = setTimeout(() => setVoice(0), 1900);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [active]);
  return (
    <Frame>
      <p className="font-display text-[13px] font-semibold text-white/85">Pick a style</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {CHIPS.map((c, i) => (
          <span key={c} className={`rounded-xl border px-3 py-2 font-display text-[14px] font-semibold transition-all duration-300 ${picked === i ? "border-transparent bg-gradient-to-br from-emerald-400 to-lime-300 text-black shadow-[0_0_24px_rgba(52,211,153,0.4)]" : "border-white/15 bg-white/[0.04] text-white/85"}`}>{c}</span>
        ))}
      </div>
      <p className="mt-4 font-display text-[13px] font-semibold text-white/85">Whose voice?</p>
      <div className="mt-2 flex gap-2">
        {["Male", "Female", "Instrumental"].map((v, i) => (
          <span key={v} className={`rounded-xl border px-3 py-2 font-display text-[14px] font-semibold transition-all duration-300 ${voice === i ? "border-transparent bg-gradient-to-r from-blue-500 to-violet-600 text-white" : "border-white/15 bg-white/[0.04] text-white/85"}`}>{v}</span>
        ))}
      </div>
    </Frame>
  );
}

function GenerateFrame({ active }) {
  const [done, setDone] = useState(0);
  useEffect(() => {
    if (!active) { setDone(0); return undefined; }
    const ids = CHECKS.map((_, i) => setTimeout(() => setDone(i + 1), 500 + i * 900));
    return () => ids.forEach(clearTimeout);
  }, [active]);
  return (
    <Frame>
      <div className="flex h-12 items-end gap-1">
        {Array.from({ length: 28 }).map((_, i) => (
          <span key={i} className="w-full rounded-sm bg-gradient-to-t from-blue-500 to-violet-400" style={{ height: `${25 + ((i * 37) % 70)}%`, animation: `demo-bar 1.1s ${(i % 7) * 0.09}s ease-in-out infinite alternate` }} />
        ))}
      </div>
      <ul className="mt-4 space-y-2">
        {CHECKS.map((c, i) => (
          <li key={c} className="flex items-center gap-2 font-display text-[14px] font-medium text-white/90">
            {done > i ? <Check className="h-4 w-4 text-emerald-300" /> : done === i ? <Loader2 className="h-4 w-4 animate-spin text-blue-300" /> : <span className="h-4 w-4 rounded-full border border-white/25" />}
            {c}
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function ReadyFrame() {
  return (
    <Frame>
      <div className="flex items-center gap-4">
        <span className="grid h-16 w-16 flex-shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-emerald-400 to-lime-300 text-black shadow-lg"><Play className="ml-0.5 h-7 w-7" /></span>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-display text-[12px] font-semibold uppercase tracking-[0.2em] text-emerald-200"><Sparkles className="h-3.5 w-3.5" /> Your song is ready</p>
          <p className="truncate font-display text-xl font-bold">Jah Is My Shelter</p>
          <p className="text-[13px] text-white/75">Gospel Reggae · 1:35 · Male voice</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[{ i: Mic2, l: "Add My Voice" }, { i: Wand2, l: "Master" }, { i: Download, l: "Download" }].map(({ i: Icon, l }) => (
          <span key={l} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.05] px-2 py-2.5 font-display text-[13px] font-semibold text-white"><Icon className="h-4 w-4 text-blue-200" />{l}</span>
        ))}
      </div>
    </Frame>
  );
}

const FRAMES = { write: WriteFrame, style: StyleFrame, generate: GenerateFrame, ready: ReadyFrame };

/** Auto-advancing, pausable walkthrough of the four steps to a finished song. */
export default function HowItWorksDemo() {
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(true);
  const timer = useRef(null);
  const current = DEMO_STEPS[step];

  useEffect(() => {
    clearTimeout(timer.current);
    if (!running) return undefined;
    timer.current = setTimeout(() => setStep((s) => (s + 1) % DEMO_STEPS.length), current.seconds * 1000);
    return () => clearTimeout(timer.current);
  }, [step, running, current.seconds]);

  const go = (i) => { setStep(i); setRunning(false); };
  const CurrentFrame = FRAMES[current.key];

  return (
    <section data-testid="home-demo" className="mt-16 grid gap-8 rounded-[32px] border border-white/[0.1] bg-[#0a0d1c]/70 p-6 sm:p-8 lg:grid-cols-[minmax(0,420px)_1fr] lg:items-center">
      <div>
        <p className="font-display text-[13px] font-semibold uppercase tracking-[0.22em] text-violet-200">How it works</p>
        <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">A finished song in four steps.</h2>
        <ol className="mt-6 space-y-2">
          {DEMO_STEPS.map((s, i) => (
            <li key={s.key}>
              <button
                type="button"
                data-testid={`demo-step-${s.key}`}
                aria-current={i === step ? "step" : undefined}
                onClick={() => go(i)}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${i === step ? "border-blue-400/50 bg-blue-500/[0.12]" : "border-transparent hover:bg-white/[0.05]"}`}
              >
                <span className={`font-display text-[15px] font-semibold ${i === step ? "text-white" : "text-white/85"}`}>{s.title}</span>
                <span className={`mt-1 block text-[14px] leading-snug ${i === step ? "text-white/85" : "text-white/65"}`}>{s.body}</span>
                {i === step && running ? (
                  <span className="mt-2 block h-1 w-full overflow-hidden rounded-full bg-white/10">
                    <span key={step} className="block h-full rounded-full bg-gradient-to-r from-blue-400 to-violet-400" style={{ animation: `demo-progress ${s.seconds}s linear forwards` }} />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button type="button" data-testid="demo-toggle" onClick={() => setRunning((r) => !r)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 font-display text-[13px] font-semibold text-white hover:bg-white/[0.12]">
            {running ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Play the walkthrough</>}
          </button>
          <Link to="/create/song" data-testid="demo-try-now" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-2 font-display text-[14px] font-bold text-white shadow-[0_8px_28px_rgba(99,102,241,0.4)] transition-transform hover:scale-[1.03]">
            Try it now <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <div className="relative min-h-[300px]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={current.key} data-testid={`demo-frame-${current.key}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
            <CurrentFrame active />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
